import { organizationClient } from 'shared/domain/organization/organizationClient';
import { OrganizationModel } from 'shared/domain/organization/types/model';
import { setLimitedMapEntry } from 'shared/utils/limitedCache';

const MAX_SIGNED_LOGO_CACHE_ENTRIES = 100;

/** Signed URL per logo id; a logo is signed once per page load. */
const signedLogoUrls = new Map<string, string>();
const pendingSignedLogoUrls = new Map<string, Promise<string | undefined>>();

/** The logo's signed URL; missing when signing failed (the PDF header goes without the logo). */
function fetchLogoSignedUrl(organizationId: string, logoId: string): Promise<string | undefined> {
  return organizationClient.getLogoSignedUrl({ organizationId, logoId }).catch(() => undefined);
}

function obtainLogoSignedUrl(organizationId: string, logoId: string): Promise<string | undefined> {
  const signedUrl = signedLogoUrls.get(logoId);
  if (signedUrl) {
    return Promise.resolve(signedUrl);
  }

  const pendingRequest = pendingSignedLogoUrls.get(logoId);
  if (pendingRequest) {
    return pendingRequest;
  }

  const request = fetchLogoSignedUrl(organizationId, logoId)
    .then((fetchedSignedUrl) => {
      if (fetchedSignedUrl) {
        setLimitedMapEntry(signedLogoUrls, logoId, fetchedSignedUrl, MAX_SIGNED_LOGO_CACHE_ENTRIES);
      }
      return fetchedSignedUrl;
    })
    .finally(() => pendingSignedLogoUrls.delete(logoId));

  pendingSignedLogoUrls.set(logoId, request);
  return request;
}

/** The organizations with the signed URL of the logo used in PDF headers (missing when signing failed). */
export function withLogoSignedUrls(organizations: OrganizationModel[]): Promise<OrganizationModel[]> {
  return Promise.all(
    organizations.map(async (organization) => ({
      ...organization,
      logoUsedInPdfHeadersSignedUrl:
        organization.logoUsedInPdfHeaders &&
        (await obtainLogoSignedUrl(organization._id, organization.logoUsedInPdfHeaders)),
    }))
  );
}
